// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Batch Traceability Page
// Interactive lineage graph (Crude PO → Tank → Batch → Refined Tank → Drum → Customer Invoice)
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/Tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { Search, GitBranch, Package, Droplets, Factory, Truck, FileText, ChevronRight, Eye, Download } from 'lucide-react';
import { useApi } from '@/shared/hooks/useApi';

interface TraceabilityNode {
  id: string;
  type: 'purchase_order' | 'tank_receipt' | 'batch' | 'output' | 'pack_run' | 'sale';
  label: string;
  code: string;
  date: string;
  qty: number;
  unit: string;
  status: string;
  children: TraceabilityNode[];
  parent?: string;
  data: any;
}

export function BatchTraceabilityPage() {
  const [traceability, setTraceability] = useState<TraceabilityNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchBatch, setSearchBatch] = useState('');
  const [activeTab, setActiveTab] = useState<'graph' | 'table' | 'timeline'>('graph');
  const { get } = useApi();
  
  async function fetchTraceability(batchNo: string) {
    setLoading(true);
    try {
      const res = await get(`/api/manufacturing/traceability/${batchNo}`);
      setTraceability(res.traceability);
    } catch (error) {
      console.error('Failed to fetch traceability:', error);
      setTraceability(null);
    } finally {
      setLoading(false);
    }
  }
  
  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchBatch.trim()) {
      fetchTraceability(searchBatch.trim());
    }
  }
  
  function renderGraph(node: TraceabilityNode, depth = 0): React.ReactNode {
    const typeConfig: Record<string, { icon: any; color: string; bgColor: string }> = {
      purchase_order: { icon: FileText, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
      tank_receipt: { icon: Droplets, color: 'text-cyan-600', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20' },
      batch: { icon: Factory, color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
      output: { icon: Package, color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-900/20' },
      pack_run: { icon: Package, color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
      sale: { icon: Truck, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-900/20' },
    };
    
    const config = typeConfig[node.type] || { icon: GitBranch, color: 'text-gray-600', bgColor: 'bg-gray-50 dark:bg-gray-800' };
    const Icon = config.icon;
    
    return (
      <div key={node.id} className="relative">
        <div className={`flex items-center gap-2 p-3 rounded-lg border ${config.bgColor} dark:border-gray-700 transition-all hover:shadow-md`} style={{ marginLeft: `${depth * 40}px` }}>
          <Icon className={`w-5 h-5 ${config.color}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white truncate">{node.label}</span>
              <Badge variant="secondary" className="text-xs">{node.code}</Badge>
              <Badge variant={node.status === 'completed' || node.status === 'passed' ? 'success' : 'outline'} className="text-xs">{node.status}</Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>{node.date ? new Date(node.date).toLocaleDateString() : 'N/A'}</span>
              <span>{node.qty.toLocaleString()} {node.unit}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-6 w-6"><Eye className="w-3 h-3" /></Button>
            <Button variant="ghost" size="icon" className="h-6 w-6"><Download className="w-3 h-3" /></Button>
          </div>
        </div>
        
        {node.children.length > 0 && (
          <div className="pl-4 border-l-2 border-gray-200 dark:border-gray-700 mt-2 space-y-2">
            {node.children.map(child => renderGraph(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }
  
  function renderTableRows(node: TraceabilityNode, depth = 0): React.ReactNode[] {
    const rows: React.ReactNode[] = [
      <TableRow key={node.id} className={depth > 0 ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
        <TableCell className="font-mono text-sm">{node.code}</TableCell>
        <TableCell>{node.label}</TableCell>
        <TableCell><Badge variant="secondary">{node.type.replace('_', ' ')}</Badge></TableCell>
        <TableCell>{node.date ? new Date(node.date).toLocaleDateString() : '-'}</TableCell>
        <TableCell>{node.qty.toLocaleString()} {node.unit}</TableCell>
        <TableCell><Badge variant={node.status === 'completed' || node.status === 'passed' ? 'success' : 'outline'}>{node.status}</Badge></TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon"><Eye className="w-3 h-3" /></Button>
          </div>
        </TableCell>
      </TableRow>
    ];
    
    node.children.forEach(child => {
      rows.push(...renderTableRows(child, depth + 1));
    });
    
    return rows;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Batch Traceability</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Interactive lineage graph (Crude PO → Tank → Batch → Refined Tank → Drum → Customer Invoice)</p>
        </div>
      </div>
      
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSearch} className="flex items-center gap-4">
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Enter batch number (e.g., BATCH-2026-088, CRUDE-2026-001)..."
                value={searchBatch}
                onChange={e => setSearchBatch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? 'Searching...' : 'Trace Batch'}
            </Button>
            {traceability && (
              <Button variant="outline" onClick={() => { setTraceability(null); setSearchBatch(''); }}>
                Clear
              </Button>
            )}
          </form>
        </div>
        
        <div className="p-4">
          {loading ? (
            <div className="text-center py-12">Searching traceability...</div>
          ) : traceability ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="graph"><GitBranch className="w-4 h-4 mr-2" /> Lineage Graph</TabsTrigger>
                <TabsTrigger value="table"><Table className="w-4 h-4 mr-2" /> Table View</TabsTrigger>
                <TabsTrigger value="timeline"><ChevronRight className="w-4 h-4 mr-2" /> Timeline</TabsTrigger>
              </TabsList>
              
              <TabsContent value="graph" className="p-4">
                <div className="space-y-4">
                  {renderGraph(traceability)}
                </div>
              </TabsContent>
              
              <TabsContent value="table" className="p-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renderTableRows(traceability)}
                  </TableBody>
                </Table>
              </TabsContent>
              
              <TabsContent value="timeline" className="p-4">
                <div className="space-y-4">
                  {(() => {
                    const events: any[] = [];
                    function collectEvents(node: TraceabilityNode) {
                      events.push({
                        date: node.date,
                        type: node.type,
                        label: node.label,
                        code: node.code,
                        qty: node.qty,
                        unit: node.unit,
                        status: node.status,
                      });
                      node.children.forEach(collectEvents);
                    }
                    collectEvents(traceability);
                    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                  })().map((event, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{idx + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">{event.label}</span>
                          <Badge variant="secondary" className="text-xs">{event.type.replace('_', ' ')}</Badge>
                          <Badge variant={event.status === 'completed' || event.status === 'passed' ? 'success' : 'outline'} className="text-xs">{event.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <span>{event.date ? new Date(event.date).toLocaleString() : 'N/A'}</span>
                          <span>{event.qty.toLocaleString()} {event.unit}</span>
                          <span className="font-mono">{event.code}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-center py-12">
              <GitBranch className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Enter a batch number to trace</h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Search for any batch number to see its complete lineage from crude purchase to customer sale</p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                  <p className="font-medium">Crude Receipt</p>
                  <p className="text-gray-500">CRUDE-2026-001</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                  <p className="font-medium">Distillation Batch</p>
                  <p className="text-gray-500">BATCH-2026-088</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                  <p className="font-medium">Pack Run</p>
                  <p className="text-gray-500">PACK-BATCH-2026-088</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                  <p className="font-medium">Customer Sale</p>
                  <p className="text-gray-500">INV-2026-XXXX</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}